import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import type { ProductEngagementStoresItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useProductEngagement } from '../hooks/use-product-engagement';

/** Any view, cart, wishlist add or sale in the window. */
const hasActivity = (store: ProductEngagementStoresItem) =>
  store.views > 0 ||
  store.addToCart > 0 ||
  store.wishlistAdds > 0 ||
  store.orders > 0 ||
  store.units > 0 ||
  store.revenue.amount > 0;

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <span className="flex flex-col">
      <span className="text-base font-semibold tabular-nums">{value}</span>
      <span className="text-muted-foreground text-xs">{label}</span>
    </span>
  );
}

function StoreLine({ store }: { store: ProductEngagementStoresItem }) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  return (
    <li className="flex items-baseline justify-between gap-3 border-t py-1.5 text-[13px]">
      <span className="min-w-0 truncate">{store.storeName}</span>
      <span className="text-muted-foreground shrink-0 tabular-nums">
        {t('insights.storeLine', {
          sold: format.number(store.units),
          revenue: format.money(store.revenue),
        })}
      </span>
    </li>
  );
}

/**
 * Insights in the master page's rail: units sold and views over the window, summed across stores,
 * and each active store's revenue on its own line. Revenue is never summed: stores sell in their
 * own currencies. Stores with nothing to show share one muted line. Needs `reports.read`; gate it.
 */
export function ProductEngagementSection({ productId }: { productId: string }) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const query = useProductEngagement(productId);
  const stores = query.data?.stores ?? [];
  const active = stores.filter(hasActivity);
  const quiet = stores.filter((store) => !hasActivity(store));
  const sold = stores.reduce((sum, store) => sum + store.units, 0);
  const views = stores.reduce((sum, store) => sum + store.views, 0);
  const days = { count: query.data?.days ?? 0 };

  if (query.isSuccess && stores.length === 0) return null;

  return (
    <SummaryCard
      collapsible
      title={t('insights.title')}
      meta={query.isSuccess ? t('insights.soldCount', { sold: format.number(sold) }) : undefined}
    >
      {query.isPending ? (
        <Skeleton className="h-16" aria-busy="true" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : active.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">{t('engagement.none', days)}</p>
      ) : (
        <>
          <span className="text-muted-foreground text-xs">{t('insights.window', days)}</span>
          <div className="flex gap-6">
            <Stat value={format.number(sold)} label={t('insights.sold')} />
            <Stat value={format.number(views)} label={t('insights.views')} />
          </div>
          <ul className="flex flex-col" aria-label={t('engagement.sectionLabel')}>
            {active.map((store) => (
              <StoreLine key={store.storeId} store={store} />
            ))}
          </ul>
          {quiet.length > 0 ? (
            <p className="text-muted-foreground text-xs">
              {t('engagement.quiet', {
                ...days,
                stores: quiet.map((store) => store.storeName).join(', '),
              })}
            </p>
          ) : null}
        </>
      )}
    </SummaryCard>
  );
}
